import { ArrowRight } from 'lucide-react';
import { useState, type FormEvent } from 'react';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

const ENDPOINT = '/API/contacto.php';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [nombre, setNombre]   = useState('');
  const [email, setEmail]     = useState('');
  const [mensaje, setMensaje] = useState('');
  const [website, setWebsite] = useState(''); // honeypot

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting' || status === 'sent') return;
    setStatus('submitting');
    setErrorMsg(null);

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, email, mensaje, website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const msg =
          data?.errors
            ? 'Revisá los campos marcados.'
            : 'No pudimos enviar el mensaje. Probá de nuevo en un minuto.';
        setErrorMsg(msg);
        setStatus('error');
        return;
      }
      setStatus('sent');
    } catch {
      setErrorMsg('No pudimos enviar el mensaje. Probá de nuevo en un minuto.');
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div className="max-w-2xl">
        <p
          className="font-display font-display-axes text-papel leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
        >
          Mensaje recibido.
        </p>
        <p
          className="font-display italic font-display-italic-axes text-papel/85 mt-3"
          style={{ fontSize: 'clamp(18px, 2vw, 24px)' }}
        >
          Te respondo en menos de 48 horas.
        </p>
      </div>
    );
  }

  const fieldBase =
    'w-full bg-transparent border-0 border-b border-papel/40 ' +
    'text-papel placeholder:text-papel/45 ' +
    'py-3 px-0 outline-none transition-colors ' +
    'focus:border-papel';
  const labelBase =
    'block font-mono text-[10px] tracking-[0.22em] uppercase text-papel/70 mb-1';

  const submitting = status === 'submitting';

  return (
    <form onSubmit={onSubmit} noValidate className="max-w-2xl flex flex-col gap-7">
      <div>
        <label htmlFor="cf-nombre" className={labelBase}>Nombre</label>
        <input
          id="cf-nombre"
          name="nombre"
          type="text"
          required
          autoComplete="name"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className={fieldBase}
          placeholder="Cómo te llamás"
          disabled={submitting}
        />
      </div>

      <div>
        <label htmlFor="cf-email" className={labelBase}>Email</label>
        <input
          id="cf-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldBase}
          placeholder="tu@dominio.com"
          disabled={submitting}
        />
      </div>

      <div>
        <label htmlFor="cf-mensaje" className={labelBase}>Mensaje</label>
        <textarea
          id="cf-mensaje"
          name="mensaje"
          required
          rows={4}
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          className={fieldBase + ' resize-none'}
          placeholder="Contame el terreno"
          disabled={submitting}
        />
      </div>

      {/* honeypot: invisible para humanos, atrae bots */}
      <div aria-hidden className="hidden">
        <label htmlFor="cf-website">No completar</label>
        <input
          id="cf-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="pt-2 flex flex-wrap items-center gap-5">
        <button
          type="submit"
          disabled={submitting}
          className={
            'group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full ' +
            'bg-carbon text-papel font-medium text-[15px] ' +
            'transition-all hover:gap-5 disabled:opacity-60 disabled:cursor-wait'
          }
        >
          <span>{submitting ? 'Enviando…' : 'Enviar mensaje'}</span>
          <span className="w-9 h-9 rounded-full grid place-items-center bg-senal text-carbon transition-transform group-hover:scale-110">
            <ArrowRight size={16} strokeWidth={2} />
          </span>
        </button>

        {status === 'error' && errorMsg && (
          <p
            role="alert"
            className="font-mono text-[11px] tracking-[0.14em] uppercase text-papel"
          >
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
}
